import { ReactNode, useMemo, useState, useEffect, useCallback, useRef, memo, isValidElement } from 'react'
import { useTranslations } from 'next-intl'
import {
    useReactTable,
    getCoreRowModel,
    flexRender,
    getSortedRowModel,
    SortingState,
    ColumnDef,
    ColumnSort,
    Header,
    getFilteredRowModel,
    RowSelectionState,
    createColumnHelper,
    Cell
} from '@tanstack/react-table'
import LoadingButton from './LoadingButton'
import Paragraph from './Paragraph'
import { isEmpty, isNil } from 'lodash'
import Icon from './Icon'
import { Info, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react'
import Tooltip from './Tooltip'
import { SortingRule } from '@/shared/models/queryModels'
import { cva, cx, VariantProps } from 'class-variance-authority'
import Link from './Link'
import IndeterminateCheckbox from './IndeterminateCheckbox'
import { Currency } from '@/shared/models/moneyModel'
import { useLocalization } from '@/shared/hooks/useLocalization'
import { NestedKeyOf, getNestedValue } from '@/shared/utils/objectUtils'
import BalanceBadge from '@/shared/components/common/BalanceBadge/BalanceBadge'
import { maskAccountNumber } from '@/shared/utils/stringUtils'

export const ICON_COLUMN_WIDTH = 30
export const XSMALL_COLUMN_WIDTH = 50
export const SMALL_COLUMN_WIDTH = 200
export const MEDIUM_COLUMN_WIDTH = 300
export const LARGE_COLUMN_WIDTH = 500

type TextConfigValueType<Data extends object> = NestedKeyOf<Data> | ((row: Data) => string | undefined)
type AmountConfigValueType<Data extends object> = NestedKeyOf<Data> | ((row: Data) => number | undefined)
type CurrencyConfigValueType<Data extends object> = NestedKeyOf<Data> | ((row: Data) => Currency | undefined)
type DateConfigValueType<Data extends object> = NestedKeyOf<Data> | ((row: Data) => string | undefined)

type AmountConfig<Data extends object> = {
    value: AmountConfigValueType<Data>
    currency: CurrencyConfigValueType<Data>
    status?: TextConfigValueType<Data>
}

type DateConfig<Data extends object> = {
    value: DateConfigValueType<Data>
    hasHours?: boolean
}

type TextConfig<Data extends object> = {
    value: TextConfigValueType<Data>
    before?: (row: Data) => ReactNode
    after?: (row: Data) => ReactNode
}

type AccountNumberConfig<Data extends object> = {
    value: NestedKeyOf<Data> | ((row: Data) => string | undefined)
}

type EnhancedColumnDef<Data extends object> = ColumnDef<Data> & {
    accessorKey?: NestedKeyOf<Data>
    isLoading?: boolean
    cellType?: 'text' | 'date' | 'custom' | 'multiLineText' | 'amount' | 'balance' | 'accountNumber'
} & (
        | { cellType?: 'custom' }
        | { cellType: 'text'; data?: TextConfig<Data> }
        | { cellType: 'amount'; data: AmountConfig<Data> }
        | { cellType: 'date'; data: DateConfig<Data> }
        | { cellType: 'balance'; data: AmountConfig<Data> }
        | { cellType: 'accountNumber'; data: AccountNumberConfig<Data> }
        | {
              cellType: 'multiLineText'
              textConfig?: {
                  primaryField?: keyof Data | undefined
                  secondaryField?: keyof Data
              }
          }
    )

export type TableProps<Data extends object> = {
    columns: EnhancedColumnDef<Data>[]
    data: Data[] | undefined
    emptyState: ReactNode
    isLoading: boolean
    skeletonRows?: number
    loadMore?: {
        isFetchingNextPage: boolean
        hasNextPage: boolean | undefined
        fetchNextPage: () => void
    }
    align?: {
        left?: string[]
        right?: string[]
        center?: string[]
    }
    action?: ReactNode
    onRow?: (row: Data) => void
    onSortBy?: (sortingRules: SortingRule<Data>[]) => void
    initialSortBy?: SortingRule<Data>[]
    getRowHref?: (row: Data) => string
    onRowSelectionChange?: (selectedRows: Data[]) => void
    selectedRows?: Data[]
    rowId?: keyof Data | ((row: Data) => string)
    getRowClassName?: (row: Data) => string
}

type AlignmentConfig = {
    right?: string[]
    center?: string[]
}

const tableHeaderVariants = cva(
    'flex items-center leading-none py-2 px-4 h-full font-medium text-textSecondary text-[11px] ',
    {
        variants: {
            sortable: {
                true: 'cursor-pointer ',
                false: ''
            },
            sortDirection: {
                asc: 'bg-backgroundSecondaryHover rounded-2 text-textSelect',
                desc: 'bg-backgroundSecondaryHover rounded-2 text-textSelect',
                none: ''
            },
            alignment: {
                left: 'justify-between',
                center: 'justify-center',
                right: 'justify-end'
            }
        },
        defaultVariants: {
            sortable: false,
            sortDirection: 'none',
            alignment: 'left'
        }
    }
)

type TableHeaderVariants = VariantProps<typeof tableHeaderVariants>

const convertSortingRuleToColumnSort = <Data extends object>(sortingRule: SortingRule<Data>): ColumnSort => ({
    id: sortingRule.id as string,
    desc: sortingRule.desc ?? false
})

const convertColumnSortToSortingRule = <Data extends object>(columnSort: ColumnSort): SortingRule<Data> => ({
    id: columnSort.id as keyof Data & string,
    desc: columnSort.desc
})

const fallbackData: [] = []

const SkeletonCell = () => <div data-testid="__table-skeleton-cell__" className="h-4 bg-gray-200 w-full rounded" />

const MemoizedSelectionCell = memo(
    ({
        checked,
        onClick,
        testId
    }: {
        checked: boolean
        onClick: (e: React.MouseEvent<HTMLDivElement>) => void
        testId: string
    }) => (
        <div className="flex justify-center items-center">
            <IndeterminateCheckbox
                checked={checked}
                onClick={onClick}
                data-testid={testId}
                aria-label={checked ? 'Deselect row' : 'Select row'}
            />
        </div>
    )
)

const MemoizedHeaderSelectionCell = memo(
    ({
        checked,
        indeterminate,
        onClick
    }: {
        checked: boolean
        indeterminate: boolean
        onClick: (e: React.MouseEvent<HTMLDivElement>) => void
    }) => (
        <div className="flex justify-center items-center">
            <IndeterminateCheckbox
                checked={checked}
                indeterminate={indeterminate}
                onClick={onClick}
                data-testid="__selection-header__"
                aria-label={checked ? 'Deselect all rows' : 'Select all rows'}
            />
        </div>
    )
)

function Table<Data extends object>({
    columns,
    data,
    emptyState,
    isLoading,
    loadMore,
    skeletonRows = 20,
    align,
    action,
    onRow,
    initialSortBy,
    onSortBy,
    getRowHref,
    onRowSelectionChange,
    selectedRows,
    rowId,
    getRowClassName
}: TableProps<Data>) {
    const t = useTranslations('root')
    const [sorting, setSorting] = useState<SortingState>(initialSortBy?.map(convertSortingRuleToColumnSort) || [])
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
    const selectionRef = useRef(new Map<string, Data>())
    const allRowsRef = useRef(new Map<string, Data>())
    const isRowSelectionEnabled = !isNil(onRowSelectionChange)
    const translations = useTranslations('root')
    const { localizeAmounts, localizeDate } = useLocalization()

    const getRowId = useCallback(
        (row: Data, index: number): string => {
            if (rowId) {
                if (typeof rowId === 'function') {
                    return rowId(row)
                } else if (typeof row[rowId] !== 'undefined') {
                    return String(row[rowId])
                }
            }

            const keys = Object.keys(row)
            if (keys.length > 0) {
                const firstKey = keys[0] as keyof Data
                if (typeof row[firstKey] !== 'undefined') {
                    return String(row[firstKey])
                }
            }
            return String(index)
        },
        [rowId]
    )

    useEffect(() => {
        if (data) {
            allRowsRef.current.clear()

            data.forEach((row, index) => {
                const id = getRowId(row, index)
                allRowsRef.current.set(id, row)
            })
        }
    }, [data, getRowId])

    useEffect(() => {
        if (isRowSelectionEnabled) {
            const newRowSelection: RowSelectionState = {}
            selectionRef.current.clear()

            if (selectedRows && selectedRows.length > 0) {
                selectedRows.forEach((row) => {
                    const id = getRowId(row, 0)
                    newRowSelection[id] = true
                    selectionRef.current.set(id, row)
                })
            }

            setRowSelection(newRowSelection)
        }
    }, [selectedRows, getRowId, isRowSelectionEnabled])

    const columnHelper = createColumnHelper<Data>()

    const selectionColumnDef = useMemo(
        () =>
            columnHelper.display({
                id: 'selection',
                header: ({ table }) => {
                    const hasSelectedRows =
                        Object.keys(rowSelection).length > 0 && Object.keys(rowSelection).some((id) => rowSelection[id])

                    return (
                        <MemoizedHeaderSelectionCell
                            checked={hasSelectedRows && table.getIsAllRowsSelected()}
                            indeterminate={
                                hasSelectedRows && table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()
                            }
                            onClick={(e) => {
                                e.stopPropagation()
                                table.getToggleAllRowsSelectedHandler()(e)
                            }}
                        />
                    )
                },
                cell: ({ row }) => (
                    <MemoizedSelectionCell
                        checked={row.getIsSelected()}
                        onClick={(e) => {
                            e.stopPropagation()
                            row.getToggleSelectedHandler()(e)
                        }}
                        testId={`__row-selection-${row.id}__`}
                    />
                ),
                size: ICON_COLUMN_WIDTH,
                enableSorting: false
            }),
        [columnHelper, rowSelection]
    )

    const allColumns = useMemo(() => {
        if (isRowSelectionEnabled) {
            return [selectionColumnDef, ...columns]
        }
        return columns
    }, [isRowSelectionEnabled, selectionColumnDef, columns])

    const memoizedData = useMemo(() => data, [data])

    const onRowSelectionChangeHandler = useCallback(
        (newRowSelection: RowSelectionState) => {
            setRowSelection(newRowSelection)

            if (data) {
                const currentSelection = new Set(Object.keys(newRowSelection).filter((id) => newRowSelection[id]))

                selectionRef.current.forEach((_, id) => {
                    if (!currentSelection.has(id)) {
                        selectionRef.current.delete(id)
                    }
                })

                data.forEach((row, index) => {
                    const id = getRowId(row, index)
                    if (newRowSelection[id]) {
                        selectionRef.current.set(id, row)
                    }
                })
            }

            if (onRowSelectionChange) {
                const selectedRows: Data[] = Array.from(selectionRef.current.values())
                onRowSelectionChange(selectedRows)
            }
        },
        [data, getRowId, onRowSelectionChange]
    )

    const convertSorting = useCallback(
        (updater: SortingState | ((prev: SortingState) => SortingState)) => {
            const newSorting = typeof updater === 'function' ? updater(sorting) : updater
            if (isEmpty(newSorting)) {
                const parsedSorting = sorting.map((sort) => ({
                    ...sort,
                    desc: !sort.desc
                }))
                setSorting(parsedSorting)
                onSortBy?.(parsedSorting.map(convertColumnSortToSortingRule))
            } else {
                setSorting(newSorting)
                onSortBy?.(newSorting.map(convertColumnSortToSortingRule))
            }
        },
        [sorting, onSortBy]
    )

    const table = useReactTable<Data>({
        columns: allColumns,
        data: isLoading ? [] : (memoizedData ?? fallbackData),
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onSortingChange: convertSorting,
        enableRowSelection: isRowSelectionEnabled,
        state: {
            sorting,
            rowSelection
        },
        onRowSelectionChange: (updaterOrValue) => {
            const newRowSelection = typeof updaterOrValue === 'function' ? updaterOrValue(rowSelection) : updaterOrValue
            onRowSelectionChangeHandler(newRowSelection)
        },
        manualSorting: Boolean(onSortBy),

        enableMultiRowSelection: true,
        getRowId: isRowSelectionEnabled ? (originalRow, index) => getRowId(originalRow, index) : undefined
    })

    const getAlignmentClass = useCallback(
        (columnId: string) => {
            if (align?.right?.includes(columnId)) return 'flex justify-end'
            if (align?.center?.includes(columnId)) return 'flex justify-center'
            return 'flex justify-start'
        },
        [align]
    )

    const renderCell = useCallback(
        (cell: Cell<Data, unknown>, column: EnhancedColumnDef<Data>) => {
            const columnIsLoading = column.isLoading
            const cellWidth = cell.column.getSize()

            if (columnIsLoading) {
                return <SkeletonCell />
            }

            const content = flexRender(cell.column.columnDef.cell, cell.getContext())

            switch (column.cellType) {
                case 'text': {
                    const { value, before, after } = column.data ?? {}
                    const row = cell.row.original

                    let textContent = undefined

                    if (typeof value === 'function') {
                        textContent = value(row)
                    } else if (value) {
                        textContent = getNestedValue(row, value, { fallback: '-' })
                    } else if (column.accessorKey) {
                        textContent = getNestedValue(row, column.accessorKey, { fallback: '-' })
                    } else {
                        const cellValue = content
                        if (typeof cellValue === 'string' || typeof cellValue === 'number') {
                            textContent = String(cellValue)
                        } else if (isValidElement(cellValue)) {
                            textContent = cellValue
                        } else if (!isNil(cellValue)) {
                            textContent = String(cellValue)
                        }
                    }

                    if (isNil(textContent) || (typeof textContent === 'string' && isEmpty(textContent.trim()))) {
                        textContent = '-'
                    }

                    return (
                        <TextCell
                            content={textContent}
                            before={before?.(row)}
                            after={after?.(row)}
                            cellWidth={cell.column.getSize()}
                        />
                    )
                }
                case 'amount': {
                    const rowData = cell.row.original
                    const amount = getNestedValue(rowData, column.data.value)
                    const currency = getNestedValue(rowData, column.data.currency)
                    const status = column.data.status ? getNestedValue(rowData, column.data.status) : undefined

                    if (status === 'PAYMENT_FAILED' || status === 'FAILED') {
                        return <div className="line-through">{localizeAmounts(amount, currency)}</div>
                    }
                    return localizeAmounts(amount, currency)
                }
                case 'multiLineText':
                    return (
                        <MultiLineTextCell
                            cellWidth={cellWidth}
                            row={cell.row.original}
                            primaryField={column?.textConfig?.primaryField}
                            secondaryField={column?.textConfig?.secondaryField}
                        />
                    )
                case 'date': {
                    const rowData = cell.row.original
                    const date = getNestedValue(rowData, column.data.value)

                    return !isNil(date) && !isEmpty(date) ? (
                        <div className="whitespace-nowrap">
                            {localizeDate(date, {
                                dateFormatOptions: {
                                    hasHours: column.data?.hasHours ?? false
                                }
                            })}
                        </div>
                    ) : (
                        '-'
                    )
                }
                case 'balance': {
                    const rowData = cell.row.original
                    const amount = getNestedValue(rowData, column.data.value, { fallback: 0 })
                    const currency = getNestedValue(rowData, column.data.currency)

                    return <BalanceBadge balance={amount} currency={currency} />
                }

                case 'accountNumber': {
                    const rowData = cell.row.original
                    const accountNumber = getNestedValue(rowData, column.data.value)
                    return !isNil(accountNumber) && !isEmpty(accountNumber) ? maskAccountNumber(accountNumber) : '-'
                }

                case 'custom':
                default:
                    return content
            }
        },
        [localizeDate, localizeAmounts]
    )

    const getSortingIcon = useCallback((column: Header<Data, unknown>['column']): ReactNode => {
        const sortDirection = column.getIsSorted()
        if (sortDirection === false) {
            return <Icon icon={ChevronsUpDown} size="2" color="disabled" />
        }
        return sortDirection === 'asc' ? <Icon icon={ChevronUp} size="2" /> : <Icon icon={ChevronDown} size="2" />
    }, [])

    const getSortDirectionVariant = useCallback(
        (sortDirection: false | 'asc' | 'desc'): TableHeaderVariants['sortDirection'] => {
            if (!sortDirection) return 'none'
            return sortDirection
        },
        []
    )

    const getColumnAlignment = useCallback(
        (columnId: string, alignConfig?: AlignmentConfig): TableHeaderVariants['alignment'] => {
            if (alignConfig?.right?.includes(columnId)) return 'right'
            if (alignConfig?.center?.includes(columnId)) return 'center'
            return 'left'
        },
        []
    )

    const handleSort = useCallback((header: Header<Data, unknown>) => {
        if (header.column.columnDef.enableSorting) {
            header.column.toggleSorting()
        }
    }, [])

    const handleRowClick = useCallback(
        (row: Data) => {
            if (onRow) {
                onRow(row)
            }
        },
        [onRow]
    )

    const isRowSelected = useCallback(
        (row: Data): boolean => {
            const rowIdentifier = getRowId(row, 0)
            return !!rowSelection[rowIdentifier]
        },
        [getRowId, rowSelection]
    )

    const TableHeader = useMemo(
        () => (
            <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id} className="bg-backgroundSecondary">
                        {headerGroup.headers.map((header) => {
                            const column = header.column.columnDef as EnhancedColumnDef<Data>
                            const isSortable = Boolean(column.enableSorting) !== false
                            const sortDirection = header.column.getIsSorted()
                            return (
                                <th
                                    key={header.id}
                                    className={`h-[40px] border-b-0.5 whitespace-nowrap ${
                                        isSortable ? 'cursor-pointer' : ''
                                    } ${isRowSelectionEnabled ? '' : 'p-[2px]'}`}
                                    style={{ width: column.size }}
                                    onClick={() => {
                                        if (isSortable) handleSort(header)
                                    }}
                                >
                                    <div
                                        className={tableHeaderVariants({
                                            sortable: isSortable,
                                            sortDirection: getSortDirectionVariant(sortDirection),
                                            alignment: getColumnAlignment(header.id, align)
                                        })}
                                    >
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                        {isSortable ? (
                                            <span data-testid={`__header-sort-${header.column.id}__`} className="ml-1">
                                                {getSortingIcon(header.column)}
                                            </span>
                                        ) : null}
                                    </div>
                                </th>
                            )
                        })}
                    </tr>
                ))}
            </thead>
        ),
        [table, isRowSelectionEnabled, handleSort, getSortDirectionVariant, getColumnAlignment, align, getSortingIcon]
    )

    const TableBody = useMemo(() => {
        if (isLoading) {
            return (
                <tbody>
                    {Array(skeletonRows)
                        .fill(null)
                        .map((_, index) => (
                            <tr key={index} className="animate-pulse" data-testid="__table-skeleton__">
                                {allColumns.map((column, colIndex) => (
                                    <td
                                        key={colIndex}
                                        className="h-[52px] py-3 px-5 font-normal text-textPrimary text-sm relative"
                                        style={{ width: column.size }}
                                    >
                                        <SkeletonCell />
                                    </td>
                                ))}
                            </tr>
                        ))}
                </tbody>
            )
        }

        if (!isNil(data) && data.length > 0) {
            return (
                <tbody>
                    {table.getRowModel().rows.map((row) => {
                        const rowUrl = getRowHref?.(row.original)
                        const isSelected = isRowSelected(row.original)
                        const rowBaseClassName = getRowClassName?.(row.original) || ''

                        return (
                            <tr
                                key={row.id}
                                className={`hover:hover:bg-backgroundPrimaryHover border-b-0.5 last:border-none relative${
                                    isSelected
                                        ? ' bg-backgroundSecondaryHover hover:bg-backgroundPrimaryHover'
                                        : `${!isEmpty(rowBaseClassName) ? ` ${rowBaseClassName}` : ''}`
                                } ${onRow || rowUrl ? 'cursor-pointer' : ''}`}
                                onClick={() => handleRowClick(row.original)}
                                data-testid={isSelected ? `__selected-row-${getRowId(row.original, 0)}__` : undefined}
                            >
                                {row.getVisibleCells().map((cell) => {
                                    const column = cell.column.columnDef as EnhancedColumnDef<Data>
                                    return (
                                        <td
                                            key={cell.id}
                                            className="h-[52px] py-3 px-5 font-normal text-textPrimary text-sm relative"
                                            style={{
                                                width: `${cell.column.getSize()}px`,
                                                maxWidth: `${cell.column.getSize()}px`
                                            }}
                                        >
                                            {!isNil(rowUrl) && column.id !== 'actions' && column.id !== 'selection' ? (
                                                <Link
                                                    href={rowUrl}
                                                    variant="plain"
                                                    className="absolute inset-0 flex items-center py-3 px-5"
                                                >
                                                    <div className={`w-full ${getAlignmentClass(cell.column.id)}`}>
                                                        {renderCell(cell, column)}
                                                    </div>
                                                </Link>
                                            ) : (
                                                <div className={getAlignmentClass(cell.column.id)}>
                                                    {renderCell(cell, column)}
                                                </div>
                                            )}
                                        </td>
                                    )
                                })}
                            </tr>
                        )
                    })}
                </tbody>
            )
        }

        return (
            <tbody>
                <tr>
                    <td colSpan={allColumns.length}>
                        <div className="flex items-center justify-center relative overflow-hidden bg-lines w-full">
                            <div className="absolute inset-0 opacity-10"></div>
                            <div className="flex flex-col items-center justify-center gap-2 p-12 text-gray-500">
                                <Icon icon={Info} color="secondary" />
                                <Paragraph variant="medium" className="text-center">
                                    {emptyState}
                                </Paragraph>
                            </div>
                        </div>
                    </td>
                </tr>
            </tbody>
        )
    }, [
        isLoading,
        skeletonRows,
        allColumns,
        data,
        table,
        getRowHref,
        isRowSelected,
        getRowClassName,
        onRow,
        handleRowClick,
        getRowId,
        getAlignmentClass,
        renderCell,
        emptyState
    ])

    const SelectionCount = useMemo(() => {
        if (isRowSelectionEnabled) {
            const selectedCount = Object.keys(rowSelection).filter((id) => rowSelection[id]).length
            return (
                <div className="mt-2 text-sm text-textSecondary">
                    {translations('components.table.selectionCount', {
                        count: selectedCount,
                        total: memoizedData?.length ?? 0
                    })}
                </div>
            )
        }
        return null
    }, [isRowSelectionEnabled, memoizedData?.length, rowSelection, translations])

    const LoadMoreButton = useMemo(() => {
        if (loadMore?.hasNextPage) {
            return (
                <LoadingButton
                    intent="secondary"
                    isLoading={loadMore.isFetchingNextPage}
                    onClick={loadMore.fetchNextPage}
                    className="mt-4 w-full"
                >
                    {t('loadMore.label')}
                </LoadingButton>
            )
        }
        return null
    }, [loadMore, t])

    return (
        <div className="w-full">
            <div className="overflow-x-auto border-0.5 border-borderPrimary rounded-2">
                <table className="w-full border-collapse table-auto">
                    {TableHeader}
                    {TableBody}
                </table>
            </div>
            {action && <div className="mt-4">{action}</div>}
            {LoadMoreButton}
            {SelectionCount}
        </div>
    )
}

type TextCellProps = {
    content: ReactNode
    before?: ReactNode
    after?: ReactNode
    cellWidth: number
    className?: string
    'data-testid'?: string
}

const TextCell = memo(({ content, before, after, cellWidth, className, 'data-testid': dataTestId }: TextCellProps) => {
    const textRef = useRef<HTMLDivElement>(null)
    const [isTruncated, setIsTruncated] = useState(false)
    const [hasContent, setHasContent] = useState(true)

    useEffect(() => {
        const checkTruncation = () => {
            if (textRef.current) {
                setIsTruncated(textRef.current.scrollWidth > textRef.current.clientWidth)
                if (!textRef.current.textContent?.trim()) {
                    setHasContent(false)
                } else {
                    setHasContent(true)
                }
            }
        }
        checkTruncation()
        window.addEventListener('resize', checkTruncation)
        return () => window.removeEventListener('resize', checkTruncation)
    }, [content, cellWidth])

    const safeContent = hasContent ? content : '-'

    return (
        <Tooltip
            trigger={
                <div className={cx('flex items-center gap-2 min-w-0 w-full', className)} data-testid={dataTestId}>
                    {before && <div className="flex-shrink-0">{before}</div>}
                    <div ref={textRef} className="truncate leading-normal whitespace-nowrap overflow-hidden min-w-0">
                        {safeContent}
                    </div>
                    {after && <div className="flex-shrink-0">{after}</div>}
                </div>
            }
            content={content}
            isActive={isTruncated}
        />
    )
})

const MultiLineTextCell = <Data extends object>({
    cellWidth,
    row,
    primaryField,
    secondaryField
}: {
    cellWidth: number
    row: Data
    primaryField?: keyof Data
    secondaryField?: keyof Data
}) => {
    const primaryValue = primaryField ? String(row[primaryField] || '') : ''
    const secondaryValue = secondaryField ? String(row[secondaryField] || '') : ''

    return (
        <div className="flex flex-col gap-1 max-w-full" data-testid="__table-multi-line-text-cell__">
            {primaryField && (
                <TextCell
                    data-testid="__table-multi-line-text-cell-first__"
                    content={primaryValue}
                    cellWidth={cellWidth}
                />
            )}
            {secondaryValue && (
                <TextCell
                    data-testid="__table-multi-line-text-cell-second__"
                    className="text-xs text-gray-500 truncate"
                    content={secondaryValue}
                    cellWidth={cellWidth}
                />
            )}
        </div>
    )
}

export default Table
